import { Box, BoxProps } from '@mui/material';
import { motion } from 'motion/react';
import { FunctionComponent, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { AsyncAnimationProps, directed } from '../../util/Animation';
import { mergeSx, motionStyleSheet } from '../../util/Styles';
import { GameBoardZIndex } from '../game/GameBoardZIndex';

export const FullScreen: FunctionComponent<PropsWithChildren<BoxProps>> = (props) => {
	return <Box {...props} sx={mergeSx({
		top: 0,
		left: 0,
		bottom: 0,
		right: 0,
		position: 'fixed',
	}, props.sx)}>
		{props.children}
	</Box >;
};

/** 
 * A container that maximises its size while maintaining a 16:9 aspect ratio (1080p)
 * Content can be rendered assuming a 1920x1080 resolution
 */
export const Widescreen: FunctionComponent<PropsWithChildren> = (props) => {
	const ASPECT_RATIO = 16 / 9;
	const BASE_WIDTH = 1920;
	const containerRef = useRef<HTMLDivElement>(null);
	const [scale, setScale] = useState(1);

	useEffect(function updateScale() {
		if (containerRef.current) {
			const observer = new ResizeObserver((entries) => {
				for (const entry of entries) {
					const width = entry.contentRect.width;
					setScale(width / BASE_WIDTH);
				}
			});

			observer.observe(containerRef.current);
			return () => observer.disconnect();
		}
	}, []);

	return <Box sx={{
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '100%',
		height: '100%',
		overflow: 'hidden',
	}}>
		<Box
			ref={containerRef}
			sx={{
				width: '100%',
				height: '100%',
				maxWidth: `calc(100vh * ${ASPECT_RATIO})`,
				maxHeight: `calc(100vw / ${ASPECT_RATIO})`,
				position: 'relative',
			}}
		>
			<Box sx={{
				width: `${BASE_WIDTH}px`,
				height: `${BASE_WIDTH / ASPECT_RATIO}px`,
				transform: `scale(${scale})`,
				transformOrigin: 'top left',
				position: 'absolute',
				top: 0,
				left: 0,
				// backgroundColor: 'pink',
			}}>
				{props.children}
			</Box>
		</Box>
	</Box>;
};

/** Animated letterbox effect that brings in black bars at top and bottom of screen */
export const Letterbox: FunctionComponent<AsyncAnimationProps> = (props) => {
	const translation = directed({ from: '-15%', to: 0 }, props);

	return <>
		<motion.div
			initial={{ top: translation.from }}
			animate={{ top: translation.to }}
			style={styles.letterbox} />
		<motion.div
			initial={{ bottom: translation.from }}
			animate={{ bottom: translation.to }}
			style={styles.letterbox} />
	</>;
};

const styles = motionStyleSheet({
	letterbox: {
		position: 'fixed',
		left: 0, right: 0,
		height: '15%',
		zIndex: GameBoardZIndex.Overlay,
		backgroundColor: 'black',
	},
});